import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  DatePicker,
  Progress,
  Tag,
  Spin,
  Empty,
} from "antd";
import {
  LikeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { Navigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  getVendorsByService,
  getVendorPerformance,
} from "../../api/outsourceApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { RangePicker } = DatePicker;

const SERVICE_TYPES = [
  "printing",
  "lamination",
  "casting",
  "corrugation",
  "pasting",
];

const VendorPerformance = () => {
  const isLogged = Boolean(localStorage.getItem("isLogged"));

  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allVendors, setAllVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(3, "month"),
    dayjs(),
  ]);

  useEffect(() => {
    const accessToken = localStorage.getItem("access-token");
    checkTokenExp(accessToken);
    fetchAllVendors();
  }, []);

  useEffect(() => {
    if (selectedVendor) {
      fetchPerformance();
    }
  }, [selectedVendor, dateRange]);

  /** Load vendors from all service types */
  const fetchAllVendors = async () => {
    setVendorsLoading(true);
    try {
      const promises = SERVICE_TYPES.map((st) =>
        getVendorsByService(st).catch(() => [])
      );
      const results = await Promise.all(promises);
      const vendorMap = new Map();
      results.flat().forEach((v) => {
        const list = Array.isArray(v) ? v : v?.vendors || [];
        list.forEach((vendor) => {
          if (vendor && vendor.id && !vendorMap.has(vendor.id)) {
            vendorMap.set(vendor.id, vendor);
          }
        });
      });
      // Also handle case where result is directly array of vendors
      results.forEach((res) => {
        if (Array.isArray(res)) {
          res.forEach((vendor) => {
            if (vendor && vendor.id && !vendorMap.has(vendor.id)) {
              vendorMap.set(vendor.id, vendor);
            }
          });
        }
      });
      const list = Array.from(vendorMap.values());
      setAllVendors(list);
      if (list.length > 0) {
        setSelectedVendor(list[0].id);
      }
    } catch (error) {
      toast.error("Failed to load vendors");
    }
    setVendorsLoading(false);
  };

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const data = await getVendorPerformance(selectedVendor, {
        start_date: dateRange[0].format("YYYY-MM-DD"),
        end_date: dateRange[1].format("YYYY-MM-DD"),
      });
      setPerformance(data);
    } catch (error) {
      toast.error("Failed to load performance data");
      setPerformance(null);
    }
    setLoading(false);
  };

  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }

  const vendorName =
    allVendors.find((v) => v.id === selectedVendor)?.name || "";

  return (
    <div>
      <PageTitle title="Back" />
      <Card
        title="Vendor Performance"
        extra={
          <Select
            style={{ width: 220, marginRight: 16 }}
            value={selectedVendor}
            onChange={setSelectedVendor}
            loading={vendorsLoading}
            placeholder="Select vendor"
          >
            {allVendors.map((v) => (
              <Select.Option key={v.id} value={v.id}>
                {v.name}
              </Select.Option>
            ))}
          </Select>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <RangePicker
            value={dateRange}
            onChange={(val) => val && setDateRange(val)}
            format="DD MMM YYYY"
          />
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : performance ? (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Total Jobs"
                    value={performance.total_jobs}
                    prefix={<CheckCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="On-Time Delivery"
                    value={performance.on_time_rate}
                    suffix="%"
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{
                      color:
                        performance.on_time_rate >= 90
                          ? "#52c41a"
                          : performance.on_time_rate >= 70
                          ? "#faad14"
                          : "#ff4d4f",
                    }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Quality Rate"
                    value={
                      performance.rejection_rate != null
                        ? (100 - performance.rejection_rate).toFixed(1)
                        : "-"
                    }
                    suffix="%"
                    prefix={<LikeOutlined />}
                    valueStyle={{
                      color:
                        performance.rejection_rate < 5
                          ? "#52c41a"
                          : "#ff4d4f",
                    }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Total Value"
                    value={performance.total_value}
                    prefix={<DollarOutlined />}
                    formatter={(v) => `₹${Number(v).toLocaleString()}`}
                  />
                </Card>
              </Col>
            </Row>

            {/* Quantity summary */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="Total Sent"
                  value={performance.total_sent?.toLocaleString()}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Received"
                  value={performance.total_received?.toLocaleString()}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Rejected"
                  value={performance.total_rejected?.toLocaleString()}
                  valueStyle={{
                    color:
                      performance.total_rejected > 0
                        ? "#ff4d4f"
                        : undefined,
                  }}
                />
              </Col>
            </Row>

            {/* Performance bars */}
            <Card
              size="small"
              title="Performance Scores"
              style={{ marginBottom: 24 }}
            >
              <div style={{ marginBottom: 12 }}>
                <span>On-Time Delivery</span>
                <Progress
                  percent={performance.on_time_rate || 0}
                  status={
                    performance.on_time_rate >= 80
                      ? "success"
                      : performance.on_time_rate >= 60
                      ? "normal"
                      : "exception"
                  }
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <span>Quality (100 − rejection %)</span>
                <Progress
                  percent={
                    performance.rejection_rate != null
                      ? Math.round(100 - performance.rejection_rate)
                      : 0
                  }
                  status={
                    performance.rejection_rate < 5
                      ? "success"
                      : performance.rejection_rate < 10
                      ? "normal"
                      : "exception"
                  }
                />
              </div>
            </Card>
          </>
        ) : (
          <Empty description="Select a vendor and date range to view performance" />
        )}
      </Card>
    </div>
  );
};

export default VendorPerformance;
